// src/hooks/useSecurityManager.ts
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface SecurityConfig {
  inactivityTimeout: number;
  enableActivityTracking: boolean;
}

interface SecurityState {
  isActive: boolean;
  lastActivity: number;
  timeRemaining: number;
  isWarningShown: boolean;
}

export const useSecurityManager = (config: Partial<SecurityConfig> = {}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Mémoriser la config pour éviter les re-renders
  const finalConfig = useMemo(() => ({
    inactivityTimeout: 5 * 60 * 1000, // 5 minutes
    enableActivityTracking: true,
    ...config
  }), [config.inactivityTimeout, config.enableActivityTracking]);
  
  const [securityState, setSecurityState] = useState<SecurityState>({
    isActive: true,
    lastActivity: Date.now(),
    timeRemaining: finalConfig.inactivityTimeout,
    isWarningShown: false
  });

  const inactivityTimer = useRef<number | null>(null);
  const warningTimer = useRef<number | null>(null);
  const updateTimer = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Événements à surveiller pour l'activité utilisateur (TOUS les événements utiles)
  const activityEvents = useMemo(() => [
    'mousedown', 'mousemove', 'keypress', 'keydown', 'scroll', 
    'touchstart', 'click', 'focus', 'input', 'wheel'
  ], []);

  // Journalisation sécurisée
  const logSecurityEvent = useCallback((event: string, details?: any) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      user: user?.id || 'anonymous',
      details: details || {}
    };
    
    console.info('🔒 Security Event:', logEntry);
  }, [user?.id]);

  // Gestionnaire d'activité utilisateur (GLOBAL)
  const handleUserActivity = useCallback(() => {
    if (!user || !finalConfig.enableActivityTracking) return;

    const now = Date.now();
    
    // Éviter les resets trop fréquents (throttling de 2 secondes)
    if (now - lastActivityRef.current < 2000) return;
    
    lastActivityRef.current = now;
    
    // Mettre à jour l'état
    setSecurityState(prev => ({
      ...prev,
      lastActivity: now,
      timeRemaining: finalConfig.inactivityTimeout,
      isWarningShown: false
    }));

    // Effacer les timers existants
    if (inactivityTimer.current) window.clearTimeout(inactivityTimer.current);
    if (warningTimer.current) window.clearTimeout(warningTimer.current);

    // Timer pour l'avertissement (1 minute avant déconnexion)
    warningTimer.current = window.setTimeout(() => {
      setSecurityState(prev => ({ ...prev, isWarningShown: true }));
      logSecurityEvent('INACTIVITY_WARNING');
    }, finalConfig.inactivityTimeout - 60000);

    // Timer pour la déconnexion automatique
    inactivityTimer.current = window.setTimeout(() => {
      logSecurityEvent('AUTO_LOGOUT_INACTIVITY');
      logout();
      navigate('/login');
    }, finalConfig.inactivityTimeout);
  }, [user, logout, navigate, logSecurityEvent, finalConfig]);

  // Réinitialiser manuellement le timer (pour le bouton "Rester connecté")
  const resetInactivityTimer = useCallback(() => {
    handleUserActivity();
  }, [handleUserActivity]);

  // Déconnexion manuelle avec log
  const secureLogout = useCallback(() => {
    logSecurityEvent('MANUAL_LOGOUT');
    logout();
    navigate('/login');
  }, [logout, navigate, logSecurityEvent]);

  // Vérification des permissions de rôle
  const hasPermission = useCallback((requiredRole?: 'admin' | 'farmer' | 'customer') => {
    if (!user) return false;
    if (!requiredRole) return true;

    const hasRole = 
      (requiredRole === 'admin' && user.is_admin) ||
      (requiredRole === 'farmer' && user.is_farmer) ||
      (requiredRole === 'customer' && user.is_customer);

    if (!hasRole) {
      logSecurityEvent('UNAUTHORIZED_ACCESS_ATTEMPT', { 
        requiredRole, 
        userRoles: {
          is_admin: user.is_admin,
          is_farmer: user.is_farmer,
          is_customer: user.is_customer
        }
      });
    }

    return hasRole;
  }, [user, logSecurityEvent]);

  // Mettre à jour le temps restant en continu
  useEffect(() => {
    if (!user || !finalConfig.enableActivityTracking) return;

    const startUpdateTimer = () => {
      updateTimer.current = window.setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastActivityRef.current;
        const remaining = Math.max(0, finalConfig.inactivityTimeout - elapsed);
        
        setSecurityState(prev => ({
          ...prev,
          timeRemaining: remaining
        }));
      }, 1000);
    };

    startUpdateTimer();

    return () => {
      if (updateTimer.current) {
        window.clearInterval(updateTimer.current);
      }
    };
  }, [user, finalConfig.enableActivityTracking, finalConfig.inactivityTimeout]);

  // Configuration des écouteurs d'événements GLOBAUX
  useEffect(() => {
    if (!user || !finalConfig.enableActivityTracking) return;

    console.log('🔒 Attaching global activity listeners'); // Debug

    // Attacher les événements sur le DOCUMENT (pas sur un élément spécifique)
    const attachGlobalListeners = () => {
      activityEvents.forEach(event => {
        // IMPORTANT: Attacher sur document ET window pour capturer tous les événements
        document.addEventListener(event, handleUserActivity, { 
          passive: true, // Pour les performances
          capture: true  // Capturer pendant la phase de capture
        });
        
        // Aussi sur window pour certains événements
        if (['scroll', 'resize', 'focus', 'blur'].includes(event)) {
          window.addEventListener(event, handleUserActivity, { passive: true });
        }
      });
    };

    // Écouteur pour changements de visibilité de l'onglet
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔒 Tab became visible - resetting timer'); // Debug
        handleUserActivity();
      }
    };

    // Attacher tous les listeners
    attachGlobalListeners();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialiser le timer au démarrage
    handleUserActivity();

    return () => {
      console.log('🔒 Cleaning up global activity listeners'); // Debug
      
      // Nettoyer TOUS les listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity, { capture: true });
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Nettoyer les timers
      if (inactivityTimer.current) window.clearTimeout(inactivityTimer.current);
      if (warningTimer.current) window.clearTimeout(warningTimer.current);
      if (updateTimer.current) window.clearInterval(updateTimer.current);
    };
  }, [user, finalConfig.enableActivityTracking]); // Dépendances minimales

  // Log de connexion (une seule fois)
  useEffect(() => {
    if (user) {
      logSecurityEvent('USER_LOGGED_IN', {
        userId: user.id,
        roles: {
          is_admin: user.is_admin,
          is_farmer: user.is_farmer,
          is_customer: user.is_customer
        }
      });
    }
  }, [user?.id]);

  return {
    securityState,
    secureLogout,
    hasPermission,
    resetInactivityTimer,
    logSecurityEvent,
    
    // Utilitaires pour les composants
    formatTimeRemaining: useCallback((ms: number) => {
      const minutes = Math.floor(ms / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, []),
    
    // Indicateurs de sécurité
    isSessionValid: !!user && securityState.isActive,
    isInactivityWarning: securityState.isWarningShown,
    timeUntilLogout: securityState.timeRemaining
  };
};